import React, { useState, useEffect } from 'react';
import { 
  Heart, Shield, Clock, Video, FileText, Users, Star, CheckCircle, 
  ArrowRight, Calendar, Lock, Stethoscope, Baby, Activity, 
  Sparkles, Phone, MessageCircle, Award, Zap, ChevronRight,
  Flower2, Sun, Moon, Sunrise
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const WomensHealth = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [activeLifeStage, setActiveLifeStage] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Value Propositions Data
  const valueProps = [
    {
      icon: Shield,
      title: 'Complete Privacy & Discretion',
      description: 'Your health journey is sacred. Encrypted consultations, private records, and judgment-free care.',
      color: '#1D57D5'
    },
    {
      icon: Users,
      title: 'Women-First Specialists',
      description: 'Board-certified gynecologists and women\'s health experts who truly understand your needs.',
      color: '#E91E8C'
    },
    {
      icon: Clock,
      title: '24/7 Access, Zero Wait',
      description: 'Connect with a specialist in under 60 seconds. Healthcare that fits your schedule.',
      color: '#28C08A'
    },
    {
      icon: Video,
      title: 'Consult From Anywhere',
      description: 'Video consultations from the comfort of your home. No travel, no waiting rooms.',
      color: '#F4CE7B'
    },
    {
      icon: Heart,
      title: 'Compassionate Care',
      description: 'Every consultation is personalized, respectful, and centered around your wellbeing.',
      color: '#FF6B9D'
    },
    {
      icon: Lock,
      title: 'HIPAA-Compliant Security',
      description: 'Bank-level encryption protects your most sensitive health information.',
      color: '#8B5CF6'
    },
    {
      icon: Award,
      title: 'Verified Excellence',
      description: 'Every specialist is verified, credentialed, and committed to the highest standards.',
      color: '#F97316'
    }
  ];

  // Services Categories
  const serviceCategories = [
    {
      title: 'Gynecology',
      icon: '🩺',
      color: '#E91E8C',
      services: ['Routine check-ups', 'Pap smears & screenings', 'Infection treatment', 'Contraception counseling']
    },
    {
      title: 'Pregnancy & Maternal Care',
      icon: '🤰',
      color: '#FF6B9D',
      services: ['Prenatal care', 'High-risk pregnancy', 'Postpartum support', 'Lactation guidance']
    },
    {
      title: 'PCOS / PCOD Management',
      icon: '💊',
      color: '#8B5CF6',
      services: ['Diagnosis & testing', 'Treatment plans', 'Lifestyle guidance', 'Hormonal balance']
    },
    {
      title: 'Fertility Guidance',
      icon: '🌸',
      color: '#28C08A',
      services: ['Fertility assessments', 'Ovulation tracking', 'IVF consultations', 'Preconception planning']
    },
    {
      title: 'Menstrual Health',
      icon: '📅',
      color: '#1D57D5',
      services: ['Irregular periods', 'Heavy bleeding', 'PMS management', 'Menstrual disorders']
    },
    {
      title: 'Sexual Wellness',
      icon: '💕',
      color: '#E91E8C',
      services: ['STI screening', 'Intimate health', 'Pain management', 'Wellness counseling']
    },
    {
      title: 'Breast Health',
      icon: '🎀',
      color: '#FF6B9D',
      services: ['Breast exams', 'Mammogram guidance', 'Lump evaluation', 'Cancer screening']
    },
    {
      title: 'Menopause Care',
      icon: '🌙',
      color: '#F97316',
      services: ['Symptom management', 'HRT consultations', 'Bone health', 'Wellness planning']
    },
    {
      title: 'Mental Wellness',
      icon: '🧠',
      color: '#8B5CF6',
      services: ['Anxiety & depression', 'Postpartum mental health', 'Stress management', 'Emotional support']
    },
    {
      title: 'Preventive Screenings',
      icon: '🔬',
      color: '#28C08A',
      services: ['Annual wellness exams', 'Cancer screenings', 'Health risk assessments', 'Immunizations']
    },
    {
      title: 'Hormonal Health',
      icon: '⚖️',
      color: '#1D57D5',
      services: ['Hormone testing', 'Thyroid care', 'Adrenal health', 'Metabolic balance']
    }
  ];

  // Life Stages Data
  const lifeStages = [
    {
      title: 'Adolescence',
      age: '12-18 years',
      icon: Sunrise,
      color: '#FF6B9D',
      description: 'Navigating the journey of becoming a woman with confidence and knowledge.',
      support: [
        'First period education & support',
        'Menstrual health guidance',
        'Body changes counseling',
        'Mental wellness support',
        'Vaccination guidance'
      ]
    },
    {
      title: 'Reproductive Years',
      age: '18-35 years',
      icon: Sun,
      color: '#E91E8C',
      description: 'Empowering you to take control of your reproductive health and life choices.',
      support: [
        'Contraception options',
        'Fertility awareness',
        'STI prevention & care',
        'Career & family planning',
        'Preventive screenings'
      ]
    },
    {
      title: 'Pregnancy & Postpartum',
      age: 'Any age',
      icon: Baby,
      color: '#28C08A',
      description: 'Supporting you through the beautiful journey of motherhood.',
      support: [
        'Prenatal care excellence',
        'High-risk pregnancy support',
        'Labor & delivery prep',
        'Postpartum recovery',
        'Breastfeeding guidance'
      ]
    },
    {
      title: 'Perimenopause',
      age: '40-50 years',
      icon: Flower2,
      color: '#8B5CF6',
      description: 'Understanding and embracing the natural transition with expert guidance.',
      support: [
        'Symptom management',
        'Hormonal changes support',
        'Sleep & mood care',
        'Bone health monitoring',
        'Lifestyle optimization'
      ]
    },
    {
      title: 'Menopause & Beyond',
      age: '50+ years',
      icon: Moon,
      color: '#F97316',
      description: 'Thriving in your wisdom years with vitality and grace.',
      support: [
        'HRT consultations',
        'Cardiovascular health',
        'Osteoporosis prevention',
        'Sexual wellness',
        'Holistic aging care'
      ]
    }
  ];

  // GEWO Advantages
  const advantages = [
    {
      icon: Sparkles,
      title: 'AI-Powered Symptom Guidance',
      description: 'Smart symptom checker helps you understand your concerns before you consult.'
    },
    {
      icon: Lock,
      title: 'Secure Medical Records',
      description: 'Your health history, safely stored and accessible whenever you need it.'
    },
    {
      icon: Video,
      title: 'Video Consultations in Minutes',
      description: 'Connect with specialists instantly—no appointments, no waiting.'
    },
    {
      icon: FileText,
      title: 'Digital Prescriptions',
      description: 'Get prescriptions sent directly to your preferred pharmacy.'
    },
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Healthcare that never sleeps. Get care whenever you need it.'
    },
    {
      icon: Users,
      title: 'Nationwide Network',
      description: 'Access to 10,000+ verified women\'s health specialists across the country.'
    }
  ];

  // Testimonials
  const testimonials = [
    {
      quote: "GEWO gave me the courage to finally address concerns I'd been too embarrassed to discuss. The specialist was so understanding, and I got answers I'd been seeking for years. This platform truly understands women.",
      name: "Priya M.",
      age: 34,
      location: "Mumbai",
      concern: "PCOS Management"
    },
    {
      quote: "During my high-risk pregnancy, having 24/7 access to specialists gave me peace of mind I can't put into words. The care I received was exceptional—both medically and emotionally.",
      name: "Ananya S.",
      age: 29,
      location: "Bangalore",
      concern: "Prenatal Care"
    },
    {
      quote: "Navigating menopause felt overwhelming until I found GEWO. My specialist didn't just treat symptoms—she helped me understand this beautiful transition. I feel empowered, not defeated.",
      name: "Rekha K.",
      age: 52,
      location: "Delhi",
      concern: "Menopause Support"
    }
  ];

  // Featured Specialists Data
  const specialists = [
    {
      name: "Dr. Meera Sharma",
      title: "Senior Gynecologist",
      experience: "18+ years",
      specialization: "High-Risk Pregnancy & Fertility",
      credentials: "MBBS, MD (OB-GYN), Fellowship in Reproductive Medicine",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Dr. Anjali Patel",
      title: "Women's Health Specialist",
      experience: "15+ years",
      specialization: "PCOS, Hormonal Health & Menopause",
      credentials: "MBBS, DNB (OB-GYN), Certified Menopause Practitioner",
      image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Dr. Kavitha Reddy",
      title: "Maternal-Fetal Medicine",
      experience: "20+ years",
      specialization: "Prenatal Care & Fetal Health",
      credentials: "MBBS, MS (OB-GYN), DM (Maternal-Fetal Medicine)",
      image: "https://images.unsplash.com/photo-1651008376811-b90baee60c1f?w=300&h=300&fit=crop&crop=face"
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* ========== HERO SECTION ========== */}
      <section style={{
        background: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 30%, #F5D0FE 70%, #E9D5FF 100%)',
        minHeight: isMobile ? 'auto' : '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: isSmallMobile ? '100px 16px 60px' : isMobile ? '100px 20px 80px' : '120px 40px 100px'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: isSmallMobile ? '100px' : '200px',
          height: isSmallMobile ? '100px' : '200px',
          background: 'radial-gradient(circle, rgba(233, 30, 140, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '3%',
          width: isSmallMobile ? '80px' : '150px',
          height: isSmallMobile ? '80px' : '150px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite 1s'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(233, 30, 140, 0.1)',
            border: '2px solid rgba(233, 30, 140, 0.3)',
            borderRadius: '50px',
            padding: isSmallMobile ? '8px 16px' : '12px 24px',
            marginBottom: isSmallMobile ? '24px' : '32px',
            backdropFilter: 'blur(10px)',
            marginTop: '40px',
          }}>
            <Heart size={isSmallMobile ? 16 : 20} color="#E91E8C" fill="#E91E8C" />
            <span style={{
              color: '#E91E8C',
              fontSize: isSmallMobile ? '12px' : '14px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>Women's Health Excellence</span>
          </div>

          {/* Main Headline */}
          <h1 style={{
            fontSize: isSmallMobile ? '32px' : isMobile ? '42px' : '64px',
            fontWeight: '900',
            color: '#1A1A2E',
            lineHeight: '1.1',
            letterSpacing: '-1px',
            maxWidth: '900px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '16px' : '24px'
          }}>
            Healthcare Designed for{' '}
            <span style={{
              background: 'linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Every Woman
            </span>
          </h1>

          {/* Subheadline */}
          <p style={{
            fontSize: isSmallMobile ? '16px' : isMobile ? '18px' : '22px',
            color: '#4A4A6A',
            lineHeight: '1.6',
            maxWidth: '700px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '24px' : '36px',
            padding: '0 8px'
          }}>
            From your first period to menopause and beyond—compassionate, private, 
            expert care that honors <strong>your journey</strong> at every stage.
          </p>

          {/* Trust Statement */}
          <p style={{
            fontSize: isSmallMobile ? '13px' : '15px',
            color: '#6B7280',
            maxWidth: '600px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '32px' : '40px'
          }}>
            🔒 100% Private & Secure • 👩‍⚕️ Board-Certified Specialists • ⚡ Connect in 60 Seconds
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '16px',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: isSmallMobile ? '40px' : '60px'
          }}>
            <button style={{
              background: 'linear-gradient(135deg, #E91E8C 0%, #C026D3 100%)',
              color: 'white',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '18px 36px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(233, 30, 140, 0.35)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '52px'
            }}>
              <Video size={20} />
              Consult a Women's Health Specialist
            </button>

            <button style={{
              background: 'white',
              color: '#E91E8C',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '18px 36px',
              borderRadius: '50px',
              border: '2px solid #E91E8C',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '52px'
            }}>
              <Calendar size={20} />
              Book an Appointment
            </button>
          </div>

          {/* Stats Row */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isSmallMobile ? '20px' : '48px',
            padding: isSmallMobile ? '24px 20px' : '32px 40px',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: isSmallMobile ? '28px' : '36px', fontWeight: '800', color: '#E91E8C' }}>500K+</div>
              <div style={{ fontSize: isSmallMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>Women Served</div>
            </div>
            {!isSmallMobile && <div style={{ width: '1px', height: '40px', background: '#E5E7EB' }} />}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: isSmallMobile ? '28px' : '36px', fontWeight: '800', color: '#8B5CF6' }}>10,000+</div>
              <div style={{ fontSize: isSmallMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>Verified Specialists</div>
            </div>
            {!isSmallMobile && <div style={{ width: '1px', height: '40px', background: '#E5E7EB' }} />}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: isSmallMobile ? '28px' : '36px', fontWeight: '800', color: '#28C08A' }}>4.9★</div>
              <div style={{ fontSize: isSmallMobile ? '12px' : '14px', color: '#6B7280', fontWeight: '500' }}>Patient Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHY WOMEN CHOOSE GEWO ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)',
              border: '2px solid #E91E8C',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Star size={isSmallMobile ? 14 : 18} color="#E91E8C" fill="#E91E8C" />
              <span style={{
                color: '#E91E8C',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>The GEWO Difference</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Why Women Choose{' '}
              <span style={{
                background: 'linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>GEWO</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              We've reimagined women's healthcare around what matters most: 
              trust, privacy, accessibility, and compassion.
            </p>
          </div>

          {/* Value Props Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isSmallMobile ? '16px' : '24px'
          }}>
            {valueProps.map((prop, index) => {
              const Icon = prop.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: '#FAFAFA',
                    borderRadius: '20px',
                    padding: isSmallMobile ? '24px 20px' : '32px',
                    border: '1px solid #F0F0F0',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: isSmallMobile ? '48px' : '56px',
                    height: isSmallMobile ? '48px' : '56px',
                    borderRadius: '14px',
                    background: `${prop.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Icon size={isSmallMobile ? 24 : 28} color={prop.color} />
                  </div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '17px' : '20px',
                    fontWeight: '700',
                    color: '#1A1A2E',
                    marginBottom: '8px'
                  }}>{prop.title}</h3>
                  <p style={{
                    fontSize: isSmallMobile ? '14px' : '15px',
                    color: '#6B7280',
                    lineHeight: '1.6'
                  }}>{prop.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== COMPREHENSIVE SERVICES ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #E0F2FE 0%, #DBEAFE 100%)',
              border: '2px solid #1D57D5',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Stethoscope size={isSmallMobile ? 14 : 18} color="#1D57D5" />
              <span style={{
                color: '#1D57D5',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Complete Care</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Comprehensive Women's Health{' '}
              <span style={{
                background: 'linear-gradient(135deg, #1D57D5 0%, #4F46E5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Services</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              From routine care to specialized treatment—every aspect of 
              women's health, covered with expertise and compassion.
            </p>
          </div>

          {/* Services Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '16px' : '24px'
          }}>
            {serviceCategories.map((category, index) => (
              <div
                key={index}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '20px',
                  padding: isSmallMobile ? '24px 20px' : '28px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  border: `2px solid ${category.color}20`,
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <span style={{ fontSize: isSmallMobile ? '28px' : '32px' }}>{category.icon}</span>
                  <h3 style={{
                    fontSize: isSmallMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: '#1A1A2E'
                  }}>{category.title}</h3>
                </div>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0
                }}>
                  {category.services.map((service, sIndex) => (
                    <li
                      key={sIndex}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isSmallMobile ? '13px' : '14px',
                        color: '#4A4A6A',
                        padding: '6px 0'
                      }}
                    >
                      <CheckCircle size={14} color={category.color} />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURED SPECIALISTS ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(135deg, #1A1A2E 0%, #2D2D44 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(233, 30, 140, 0.15)',
              border: '2px solid rgba(233, 30, 140, 0.3)',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Award size={isSmallMobile ? 14 : 18} color="#E91E8C" />
              <span style={{
                color: '#E91E8C',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Expert Care Team</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#FFFFFF',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Meet Our{' '}
              <span style={{
                background: 'linear-gradient(135deg, #E91E8C 0%, #FF6B9D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Featured Specialists</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Board-certified experts with decades of experience, chosen for their 
              clinical excellence and compassionate approach to care.
            </p>
          </div>

          {/* Specialists Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '20px' : '32px'
          }}>
            {specialists.map((doctor, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  padding: isSmallMobile ? '24px' : '32px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: isSmallMobile ? '100px' : '120px',
                  height: isSmallMobile ? '100px' : '120px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto 20px',
                  border: '4px solid rgba(233, 30, 140, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <h3 style={{
                  fontSize: isSmallMobile ? '18px' : '22px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  marginBottom: '4px'
                }}>{doctor.name}</h3>
                <p style={{
                  fontSize: isSmallMobile ? '13px' : '14px',
                  color: '#E91E8C',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>{doctor.title}</p>
                <p style={{
                  fontSize: isSmallMobile ? '12px' : '13px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '12px'
                }}>{doctor.experience} • {doctor.specialization}</p>
                <p style={{
                  fontSize: isSmallMobile ? '11px' : '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  lineHeight: '1.5'
                }}>{doctor.credentials}</p>
              </div>
            ))}
          </div>

          {/* What Makes GEWO Specialists Different */}
          <div style={{
            marginTop: isSmallMobile ? '40px' : '64px',
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '24px',
            padding: isSmallMobile ? '24px 20px' : '32px 40px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '20px'
          }}>
            {[
              { icon: '🎓', label: 'Board Certified' },
              { icon: '❤️', label: 'Empathy-Trained' },
              { icon: '⭐', label: 'Top 1% Rated' },
              { icon: '🔬', label: 'Research-Active' }
            ].map((item, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <span style={{ fontSize: isSmallMobile ? '28px' : '36px' }}>{item.icon}</span>
                <p style={{
                  fontSize: isSmallMobile ? '13px' : '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: '600',
                  marginTop: '8px'
                }}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== LIFE STAGES SUPPORT ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FDF2F8 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
              border: '2px solid #F97316',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Flower2 size={isSmallMobile ? 14 : 18} color="#F97316" />
              <span style={{
                color: '#F97316',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Lifelong Partner</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Supporting Women at{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F97316 0%, #E91E8C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Every Life Stage</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Your health needs evolve—and so do we. GEWO is your trusted 
              companion through every chapter of womanhood.
            </p>
          </div>

          {/* Life Stages Tabs/Cards */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '24px'
          }}>
            {/* Stage Selector */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              gap: '12px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: isMobile ? 'center' : 'flex-start'
            }}>
              {lifeStages.map((stage, index) => {
                const Icon = stage.icon;
                const isActive = activeLifeStage === index;
                return (
                  <button
                    key={index}
                    onClick={() => setActiveLifeStage(index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: isSmallMobile ? '12px 16px' : '14px 20px',
                      background: isActive ? stage.color : '#FFFFFF',
                      color: isActive ? 'white' : '#4A4A6A',
                      border: `2px solid ${isActive ? stage.color : '#E5E7EB'}`,
                      borderRadius: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontWeight: '600',
                      fontSize: isSmallMobile ? '13px' : '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Icon size={18} />
                    {stage.title}
                  </button>
                );
              })}
            </div>

            {/* Active Stage Content */}
            <div style={{
              flex: 1,
              background: '#FFFFFF',
              borderRadius: '24px',
              padding: isSmallMobile ? '28px 24px' : '40px',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
              border: `3px solid ${lifeStages[activeLifeStage].color}20`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: isSmallMobile ? '56px' : '70px',
                  height: isSmallMobile ? '56px' : '70px',
                  borderRadius: '50%',
                  background: `${lifeStages[activeLifeStage].color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {React.createElement(lifeStages[activeLifeStage].icon, {
                    size: isSmallMobile ? 28 : 36,
                    color: lifeStages[activeLifeStage].color
                  })}
                </div>
                <div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '22px' : '28px',
                    fontWeight: '800',
                    color: '#1A1A2E',
                    marginBottom: '4px'
                  }}>{lifeStages[activeLifeStage].title}</h3>
                  <span style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: lifeStages[activeLifeStage].color,
                    fontWeight: '600'
                  }}>{lifeStages[activeLifeStage].age}</span>
                </div>
              </div>

              <p style={{
                fontSize: isSmallMobile ? '15px' : '17px',
                color: '#4A4A6A',
                lineHeight: '1.7',
                marginBottom: '24px'
              }}>{lifeStages[activeLifeStage].description}</p>

              <div style={{
                background: '#FAFAFA',
                borderRadius: '16px',
                padding: isSmallMobile ? '20px' : '24px'
              }}>
                <h4 style={{
                  fontSize: isSmallMobile ? '14px' : '15px',
                  fontWeight: '700',
                  color: '#1A1A2E',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>How We Support You</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {lifeStages[activeLifeStage].support.map((item, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: isSmallMobile ? '14px' : '15px',
                        color: '#4A4A6A',
                        padding: '8px 0'
                      }}
                    >
                      <CheckCircle size={18} color={lifeStages[activeLifeStage].color} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== THE GEWO ADVANTAGE ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: 'linear-gradient(135deg, #1D57D5 0%, #4F46E5 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(244, 206, 123, 0.15)',
              border: '2px solid rgba(244, 206, 123, 0.4)',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Zap size={isSmallMobile ? 14 : 18} color="#F4CE7B" />
              <span style={{
                color: '#F4CE7B',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Technology + Care</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#FFFFFF',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              The GEWO{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Advantage</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Cutting-edge technology meets compassionate care—delivering 
              healthcare experiences women deserve.
            </p>
          </div>

          {/* Advantages Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '16px' : '24px'
          }}>
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    padding: isSmallMobile ? '24px 20px' : '32px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{
                    width: isSmallMobile ? '48px' : '56px',
                    height: isSmallMobile ? '48px' : '56px',
                    borderRadius: '14px',
                    background: 'rgba(244, 206, 123, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Icon size={isSmallMobile ? 24 : 28} color="#F4CE7B" />
                  </div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '17px' : '20px',
                    fontWeight: '700',
                    color: '#FFFFFF',
                    marginBottom: '8px'
                  }}>{advantage.title}</h3>
                  <p style={{
                    fontSize: isSmallMobile ? '14px' : '15px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: '1.6'
                  }}>{advantage.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '120px 40px',
        background: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)',
              border: '2px solid #E91E8C',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <MessageCircle size={isSmallMobile ? 14 : 18} color="#E91E8C" />
              <span style={{
                color: '#E91E8C',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Real Stories</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Women Who Trust{' '}
              <span style={{
                background: 'linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>GEWO</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Hear from the thousands of women whose lives have been 
              transformed by compassionate, accessible healthcare.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '20px' : '32px'
          }}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, #FDF2F8 0%, #FFFFFF 100%)',
                  borderRadius: '24px',
                  padding: isSmallMobile ? '28px 24px' : '36px',
                  border: '1px solid #FCE7F3',
                  position: 'relative'
                }}
              >
                {/* Quote Mark */}
                <div style={{
                  position: 'absolute',
                  top: isSmallMobile ? '20px' : '28px',
                  right: isSmallMobile ? '20px' : '28px',
                  fontSize: '48px',
                  color: '#E91E8C',
                  opacity: 0.15,
                  fontFamily: 'Georgia, serif',
                  lineHeight: 1
                }}>"</div>

                <p style={{
                  fontSize: isSmallMobile ? '15px' : '16px',
                  color: '#4A4A6A',
                  lineHeight: '1.7',
                  marginBottom: '24px',
                  fontStyle: 'italic'
                }}>"{testimonial.quote}"</p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: '1px solid #F0F0F0'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E91E8C 0%, #C026D3 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p style={{
                      fontSize: isSmallMobile ? '15px' : '16px',
                      fontWeight: '700',
                      color: '#1A1A2E'
                    }}>{testimonial.name}, {testimonial.age}</p>
                    <p style={{
                      fontSize: isSmallMobile ? '12px' : '13px',
                      color: '#6B7280'
                    }}>{testimonial.location} • {testimonial.concern}</p>
                  </div>
                </div>

                {/* Stars */}
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginTop: '12px'
                }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#F4CE7B" color="#F4CE7B" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA SECTION ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: 'linear-gradient(135deg, #E91E8C 0%, #C026D3 50%, #8B5CF6 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '120px',
          height: '120px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '8%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite 1s'
        }} />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '52px',
            fontWeight: '900',
            color: '#FFFFFF',
            marginBottom: isSmallMobile ? '16px' : '24px',
            lineHeight: '1.15'
          }}>
            Your Health Deserves the Best.{' '}
            <br />
            <span style={{ opacity: 0.9 }}>Start Your Journey Today.</span>
          </h2>

          <p style={{
            fontSize: isSmallMobile ? '16px' : '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '32px' : '40px'
          }}>
            Join <strong>500,000+ women</strong> who've discovered healthcare that truly understands them. 
            Compassionate. Private. Available when you need it.
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '16px',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <button style={{
              background: '#FFFFFF',
              color: '#E91E8C',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '20px 40px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Video size={22} />
              Consult Now — It's Free to Start
            </button>

            <button style={{
              background: 'transparent',
              color: '#FFFFFF',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '20px 40px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Phone size={20} />
              Talk to Us
            </button>
          </div>

          {/* Trust Badge */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '32px',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} color="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                100% Private & Secure
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} color="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                Available 24/7
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Heart size={18} color="white" fill="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                Trusted by Thousands
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Animation Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </>
  );
};

export default WomensHealth;
